import React, { Suspense, useEffect, useState } from 'react';
import type { ICommand } from '@uiw/react-md-editor';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Dynamically import MDEditor to avoid SSR issues
const MDEditor = React.lazy(() => import('@uiw/react-md-editor'));

interface MarkdownEditorProps {
  value: string;
  onChange: (value?: string) => void;
  placeholder?: string;
  height?: number;
  darkMode?: boolean;
  preview?: 'edit' | 'preview' | 'live';
  textareaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

// Add the underline command
const underlineCommand: ICommand = {
  name: 'underline',
  keyCommand: 'underline',
  buttonProps: { 'aria-label': 'Add underline text' },
  icon: (
    <svg width="12" height="12" viewBox="0 0 20 20">
      <path fill="currentColor" d="M10 17a6 6 0 0 0 6-6V3h-2.5v8a3.5 3.5 0 0 1-7 0V3H4v8a6 6 0 0 0 6 6zm-7 2h14v1H3z"/>
    </svg>
  ),
  execute: (state: { selectedText: string }, api: { replaceSelection: (text: string) => void }) => {
    let modifyText = `<u>${state.selectedText}</u>`;
    if (!state.selectedText) {
      modifyText = '<u>underlined text</u>';
    }
    api.replaceSelection(modifyText);
  },
};

const LoadingFallback = () => (
  <div className="flex justify-center items-center p-4 h-[300px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
  </div>
);

// Custom Markdown preview component that doesn't rely on process.cwd
const MarkdownPreview: React.FC<{ source: string }> = ({ source }) => {
  const [html, setHtml] = useState('');

  useEffect(() => {
    // Convert markdown to HTML and sanitize it
    const rawHtml = marked(source);
    const cleanHtml = DOMPurify.sanitize(rawHtml);
    setHtml(cleanHtml);
  }, [source]);

  return (
    <div 
      className="markdown-body prose dark:prose-invert max-w-none" 
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder,
  height = 300,
  darkMode = false,
  preview,
  textareaProps
}) => {
  const [commands, setCommands] = useState<ICommand[]>([]);

  useEffect(() => {
    // Dynamically import commands
    import('@uiw/react-md-editor').then((mod) => {
      const defaultCommands = [
        mod.commands.bold,
        mod.commands.italic,
        underlineCommand,
        mod.commands.strikethrough,
        mod.commands.title1,
        mod.commands.title2,
        mod.commands.title3,
        mod.commands.quote,
        mod.commands.unorderedListCommand,
        mod.commands.orderedListCommand,
        mod.commands.checkedListCommand,
        mod.commands.divider,
        mod.commands.codeBlock
      ];
      setCommands(defaultCommands);
    });
  }, []);

  // For preview mode, use our custom Markdown preview component
  if (preview === 'preview') {
    return <MarkdownPreview source={value} />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <div data-color-mode={darkMode ? "dark" : "light"}>
        <MDEditor
          value={value}
          onChange={onChange}
          preview={preview}
          height={height}
          hideToolbar={false}
          className="w-full bg-white! dark:bg-gray-800! markdown-editor-override"
          textareaProps={{
            placeholder,
            "aria-label": placeholder,
            style: { color: darkMode ? '#fff' : '#111827' },
            ...textareaProps
          }}
          previewOptions={{
            className: "text-gray-900! dark:prose-invert dark:text-white!"
          }}
          commands={commands}
        />
      </div>
    </Suspense>
  );
};

export default MarkdownEditor; 